<template>
  <!-- Footer Section -->
  <footer class="site-footer">
    <div class="footer-main">
      <div class="footer-container">
        <div class="footer-left">
          <div class="footer-logo">
            <img src="@/assets/images/logo.png" alt="BullTire Logo" class="footer-logo-image" />
          </div>
          <p class="footer-description">
            {{ $t('footer.description') }}
          </p>
        </div>

        <div class="footer-columns">
          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.productTitle') }}</h3>
            <ul class="footer-links">
              <!-- 产品列表加载中 -->
              <li v-if="productsLoading"><span class="loading-text">{{ $t('common.loading') }}</span></li>

              <!-- 产品列表错误 -->
              <li v-else-if="productsError"><span class="error-text">{{ $t('common.error') }}</span></li>

              <!-- 无产品时显示 -->
              <li v-else-if="products.length === 0"><span class="empty-text">{{ $t('common.noProducts') }}</span></li>

              <!-- 动态显示产品列表 -->
              <li v-for="product in products" :key="product.id">
                <a :href="product.jumpLink" target="_blank">{{ product.productName }}</a>
              </li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.aboutTitle') }}</h3>
            <ul class="footer-links">
              <!-- 关于我们列表加载中 -->
              <li v-if="aboutLoading"><span class="loading-text">{{ $t('common.loading') }}</span></li>

              <!-- 关于我们列表错误 -->
              <li v-else-if="aboutError"><span class="error-text">{{ $t('common.error') }}</span></li>

              <!-- 无关于我们内容时显示 -->
              <li v-else-if="aboutItems.length === 0"><span class="empty-text">{{ $t('common.empty') }}</span></li>

              <!-- 动态显示关于我们列表 -->
              <li v-for="item in formattedAboutItems" :key="item.id">
                <a v-if="item.jumpLink && item.jumpLink.startsWith('http')" :href="item.jumpLink" target="_blank">{{ item.content }}</a>
                <router-link v-else :to="item.jumpLink || '/about'">{{ item.content }}</router-link>
              </li>

            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.qrcode') }}</h3>
            <div class="qr-codes">
              <!-- QR codes loading state -->
              <div v-if="qrCodesLoading" class="loading-container">
                <span class="loading-text">{{ $t('common.loading') }}</span>
              </div>

              <!-- QR codes error state -->
              <div v-else-if="qrCodesError" class="error-container">
                <span class="error-text">{{ $t('common.error') }}</span>
              </div>

              <!-- No QR codes available -->
              <div v-else-if="qrCodes.length === 0" class="empty-container">
                <span class="empty-text">{{ $t('common.noQrcode') }}</span>
              </div>
              <!-- QR codes display -->
              <div v-for="(qrCode, index) in qrCodes" :key="qrCode.id" class="qr-code">
                <span>{{ qrCode.content }}</span>
                <div class="qr-image">
                  <img :src="getQrCodeUrl(qrCode.qrUrl)" :alt="`QR Code ${index + 1}`" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-container">
        <!-- 加载状态 -->
        <div v-if="footerBaseLoading" class="loading-text">{{ $t('common.loading') }}</div>
        <!-- 错误状态 -->
        <div v-else-if="footerBaseError" class="error-text">{{ $t('common.error') }}</div>
        <!-- 正常显示 -->
        <div v-else class="footer-info">
          <span class="copyright">{{ footerBase.footerCopyright }}</span>
          <span v-if="footerBase.footerFilingInfo" class="divider">|</span>
          <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" class="icp-link">{{ footerBase.footerFilingInfo }}</a>
          <span v-if="footerBase.footerJumpLink" class="divider">|</span>
          <a href="https://beian.mps.gov.cn/#/query/webSearch" target="_blank" class="police-link">{{ footerBase.footerJumpLink }}</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'SiteFooter',
  data() {
    return {
      // 产品相关数据
      products: [],
      productsLoading: false,
      productsError: null,

      // 关于我们相关数据
      aboutItems: [],
      aboutLoading: false,
      aboutError: null,

      // 二维码相关数据
      qrCodes: [],
      qrCodesLoading: false,
      qrCodesError: null,

      // 备案信息相关数据
      footerBase: {
        footerCopyright: '',    // 版权信息
        footerFilingInfo: '',   // ICP备案信息
        footerJumpLink: '',     // 公安备案信息
        footerContactUs: ''     // 服务热线
      },
      footerBaseLoading: false,
      footerBaseError: null
    };
  },
  computed: {
    formattedAboutItems() {
      return this.aboutItems.map(item => {
        if (item.jumpLink && !item.jumpLink.startsWith('http')) {
          return {
            ...item,
            jumpLink: `https://${item.jumpLink}`
          };
        }
        return item;
      });
    }
  },
  created() {
    this.fetchProductList();
    this.fetchAboutList();
    this.fetchQrCodes();
    this.fetchFooterBase();
  },
  methods: {
    fetchProductList() {
      this.productsLoading = true;
      this.productsError = null;

      this.$api.product.getFooterProductList({
        pageNum: 1,
        pageSize: 5 // 只显示前5个产品
      })
        .then(response => {
          if (response.code === 200) {
            this.products = response.rows || [];
          } else {
            throw new Error(response.msg || '获取产品列表失败');
          }
        })
        .catch(err => {
          console.error('获取产品列表失败:', err);
          this.productsError = '加载失败';
        })
        .finally(() => {
          this.productsLoading = false;
        });
    },

    fetchAboutList() {
      this.aboutLoading = true;
      this.aboutError = null;

      // 使用API模块调用
      this.$api.about.list({
        pageNum: 1,
        pageSize: 5 // 只显示前5条关于我们
      })
        .then(response => {
          if (response.code === 200) {
            this.aboutItems = response.rows || [];
            console.log('关于我们列表:', this.aboutItems);
          } else {
            throw new Error(response.msg || '获取关于我们列表失败');
          }
        })
        .catch(err => {
          console.error('获取关于我们列表失败:', err);
          this.aboutError = '加载失败';
        })
        .finally(() => {
          this.aboutLoading = false;
        });
    },

    fetchQrCodes() {
      this.qrCodesLoading = true;
      this.qrCodesError = null;

      this.$api.qr.getQrList()
        .then(response => {
          if (response.code === 200) {
            this.qrCodes = response.rows || [];
            // Generate QR code images for each URL
            this.qrCodes.forEach(qrCode => {
              this.generateQrImage(qrCode);
            });
          } else {
            throw new Error(response.msg || '获取二维码列表失败');
          }
        })
        .catch(err => {
          console.error('获取二维码列表失败:', err);
          this.qrCodesError = '加载失败';
        })
        .finally(() => {
          this.qrCodesLoading = false;
        });
    },

    generateQrImage(qrCode) {
      if (!qrCode.qrUrl) return;

      QRCode.toDataURL(qrCode.qrUrl, {
        width: 85,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
        .then(url => {
          // Use Vue.set to ensure reactivity
          this.$set(qrCode, 'qrImageUrl', url);
        })
        .catch(err => {
          console.error('QR码生成失败:', err);
        });
    },

    fetchFooterBase() {
      this.footerBaseLoading = true;
      this.footerBaseError = null;

      this.$api.home.getFootBase()
        .then(response => {
          if (response.code === 200 && response.data) {
            // 更新备案信息数据
            this.footerBase = {
              footerCopyright: response.data.footerCopyright || '',
              footerFilingInfo: response.data.footerFilingInfo || '',
              footerJumpLink: response.data.footerJumpLink || '',
              footerContactUs: response.data.footerContactUs || ''
            };
            console.log('备案信息:', this.footerBase);
          } else {
            throw new Error(response.msg || '获取备案信息失败');
          }
        })
        .catch(err => {
          console.error('获取备案信息失败:', err);
          this.footerBaseError = '加载失败';
        })
        .finally(() => {
          this.footerBaseLoading = false;
        });
    },

    getQrCodeUrl(url) {
      return process.env.VUE_APP_IMAGE_URL + url;
    }
  }
}
</script>

<style lang="scss" scoped>
.site-footer {
  background-color: #1a1a1a;
  color: #fff;
}

.footer-main {
  padding: 60px 0;
}

.footer-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center; /* 容器居中 */
}

.footer-left {
  flex: 1;
  min-width: 240px;
  margin-right: 60px;
  margin-bottom: 30px;
}

.footer-logo-image {
  height: 30px;
  margin-bottom: 20px;
}

.footer-description {
  color: #999;
  line-height: 1.6;
  font-size: 14px;
}

.footer-columns {
  display: flex;
  flex-wrap: wrap;
  flex: 2;
}

.footer-column {
  flex: 1;
  min-width: 160px;
  margin-bottom: 30px;
  padding-right: 20px;
}

.footer-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #fff;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 10px;
}

.footer-links a {
  color: #999;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.footer-links a:hover {
  color: #e60012;
}

.loading-text, .error-text, .empty-text {
  color: #777;
  font-size: 14px;
  font-style: italic;
}

.error-text {
  color: #ff6b6b;
}

.qr-codes {
  display: flex;
  gap: 20px;
}

.qr-code {
  display: flex;
  flex-direction: column;
}

.qr-code span {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.qr-image {
  width: 85px;
  height: 85px;
  background-color: #fff;
  border-radius: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.footer-bottom {
  background-color: #e60012;
  padding: 20px 0;
}

.footer-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.footer-info {
  text-align: center;
  color: #fff;
  font-size: 14px;
}

.footer-info .divider {
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.6);
}

.footer-info a {
  color: #fff;
  text-decoration: none;
  transition: opacity 0.3s;
}

.footer-info a:hover {
  opacity: 0.8;
}

.loading-text, .error-text {
  text-align: center;
  color: #fff;
  font-size: 14px;
}

.error-text {
  color: #ff6b6b;
}

@media (max-width: 768px) {
  .footer-description {
    text-align: left;
  }
  .footer-container {
    flex-direction: column !important;
    align-items: center;
    padding: 0 8px !important;
  }
  .footer-main {
    padding: 32px 0 !important;
  }
  .footer-left {
    margin-right: 0 !important;
    text-align: center;
    min-width: 0;
    width: 100%;
  }
  .footer-logo-image {
    margin: 0 auto 16px auto;
    height: 28px;
  }
  .footer-description {
    font-size: 13px;
    padding: 0 8px;
  }
  .footer-columns {
    flex-direction: column !important;
    align-items: center;
    width: 100%;
    gap: 0;
  }
  .footer-column {
    min-width: 0;
    width: 100%;
    padding-right: 0;
    margin-bottom: 18px;
    text-align: center;
  }
  .footer-title {
    font-size: 15px;
    margin-bottom: 12px;
  }
  .footer-links {
    font-size: 13px;
  }
  .qr-codes {
    flex-direction: row !important;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px !important;
  }
  .qr-code {
    margin-bottom: 0;
    align-items: center;
  }
  .qr-image {
    width: 70px !important;
    height: 70px !important;
  }
  .footer-bottom {
    padding: 12px 0 !important;
  }
  .footer-info {
    font-size: 12px;
    padding: 0 4px;
  }
  .footer-info .divider {
    margin: 0 4px;
  }
}
</style>
